import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import styles from '../styles/UserSettings.module.css';
import { TIMEZONES } from '../utils/constants';
import { validateEmail, validatePhone } from '../utils/validation';
import EditableField from '../components/EditableField';
import VersionFooter from '../components/VersionFooter';

const REMINDER_TIMES = [
  { value: 'week_start', label: 'Week Start' },
  { value: 'friday_night', label: 'Friday Nights' },
  { value: 'gameday_morning', label: 'Gameday Mornings' },
  { value: 'last_chance', label: 'Last Chance' }
];

const PICKSLIP_BEHAVIORS = [
  { value: 'peek', label: 'Peek' },
  { value: 'open', label: 'Open' },
  { value: 'none', label: 'None' }
];

const REMINDER_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'discord', label: 'Discord' }
];

const UserSettingsPage = () => {
  const { user, updateUser } = useContext(AuthContext);

  const [formData] = useState({
    username: user?.username || '',
    newPassword: '',
    confirmPassword: '',
    discord_username: user?.discord_username || '',
    email: user?.email || '',
    phone: user?.phone || '',
    timezone: user?.timezone || 'America/New_York',
    pickslip_behavior: user?.pickslip_behavior || 'peek',
    reminder_preferences: user?.reminder_preferences ? JSON.parse(user.reminder_preferences) : [],
    reminder_times: user?.reminder_times || [],
    default_bye_visibility: user?.default_bye_visibility || false,
    user_notes: user?.user_notes || ''
  });

  const [successMessage, setSuccessMessage] = useState('');

  const handleFieldSave = async (name, value) => {
    await updateUser({ [name]: value });
    setSuccessMessage(`${name} updated successfully`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <div className={styles.settingsContainer}>
      <h2 className={styles.title}>User Settings</h2>

      {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

      {/* Profile Section */}
      <section className={styles.section}>
        <h3>Profile Information</h3>

        <EditableField
          label="Username"
          value={user?.username || ''}
          name="username"
          onSave={handleFieldSave}
          validation={(value) => !value.trim() ? 'Username is required' : null}
        />

        <EditableField
          label="Email Address"
          value={user?.email || ''}
          name="email"
          type="email"
          onSave={handleFieldSave}
          validation={validateEmail}
        />

        <EditableField
          label="Discord Username"
          value={user?.discord_username || ''}
          name="discord_username"
          onSave={handleFieldSave}
          helperText="Discord integration coming soon"
        />

        <EditableField
          label="Phone Number"
          value={user?.phone || ''}
          name="phone"
          type="tel"
          onSave={handleFieldSave}
          validation={validatePhone}
          helperText="SMS notifications coming soon"
        />
      </section>

      {/* Password Section */}
      <section className={styles.section}>
        <h3>Change Password</h3>
        <form onSubmit={(e) => e.preventDefault()}>
          <EditableField
            key="change-password"
            label="New Password"
            value=""
            name="new-password"
            type="password"
            onSave={async (name, value) => {
              try {
                await api.resetPassword(value);
                setSuccessMessage('Password updated successfully');
                setTimeout(() => setSuccessMessage(''), 3000);
              } catch (error) {
                setSuccessMessage(`Failed to update password: ${error.message}`);
              }
            }}
            validation={(value) => value.length < 6 ? 'Password must be at least 6 characters' : null}
          />
        </form>
      </section>

      {/* Game Preferences Section */}
      <section className={styles.section}>
        <h3>Game Preferences</h3>

                {/* Pickslip Behavior */}
                <div className={styles.fieldGroup}>
          <label htmlFor="pickslip-behavior" className={styles.fieldLabel}>
            Pickslip Behavior
          </label>
          <select
            id="pickslip-behavior"
            className={styles.select}
            value={formData.pickslip_behavior}
            onChange={(e) => handleFieldSave('pickslip_behavior', e.target.value)}
          >
            {PICKSLIP_BEHAVIORS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span className={styles.helperText}>
            Controls how the pickslip responds after watching a pick:
            <br />• Peek (Default): Briefly shows the pickslip to help new users notice where their watches are stored
            <br />• Open: Keeps the pickslip open after watching, making it easier to lock picks immediately
            <br />• None: Pickslip stays hidden, ideal for watching multiple games before locking picks
          </span>
        </div>

        {/* Bye Week Visibility */}
        <div className={styles.fieldGroup}>
          <label htmlFor="bye-visibility" className={styles.fieldLabel}>
            Default Bye Week Visibility
          </label>
          <label className={styles.toggleSwitch} aria-label="Toggle bye week visibility">
            <input
              id="bye-visibility"
              type="checkbox"
              checked={formData.default_bye_visibility}
              onChange={(e) => handleFieldSave('default_bye_visibility', e.target.checked)}
            />
            <span className={styles.slider}></span>
          </label>
        </div>

        {/* Timezone Selection */}
        <div className={styles.fieldGroup}>
          <label htmlFor="timezone" className={styles.fieldLabel}>
            Timezone
          </label>
          <select
            id="timezone"
            className={styles.select}
            value={formData.timezone}
            onChange={(e) => handleFieldSave('timezone', e.target.value)}
          >
            {TIMEZONES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Reminder Methods Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Weekly Lock Reminders</h3>
          <span className={styles.futureFeature}>Coming Soon</span>
        </div>
        <div className={styles.reminderOptions}>
          {REMINDER_METHODS.map(({ value, label }) => (
            <div key={value} className={styles.fieldGroup}>
              <label htmlFor={`reminder-${value}`} className={styles.fieldLabel}>
                {label} Notifications
              </label>
              <label className={styles.toggleSwitch} aria-label={`Toggle ${label.toLowerCase()} notifications`}>
                <input
                  id={`reminder-${value}`}
                  type="checkbox"
                  checked={formData.reminder_preferences.includes(value)}
                  onChange={(e) => {
                    const newPreferences = e.target.checked
                      ? [...formData.reminder_preferences, value]
                      : formData.reminder_preferences.filter(p => p !== value);
                    handleFieldSave('reminder_preferences', newPreferences);
                  }}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          ))}

          <div className={styles.reminderTimes}>
            <label htmlFor="reminder-times-group" className={styles.fieldLabel}>
              When to Send Reminders
            </label>
            <div id="reminder-times-group" className={styles.checkboxGroup}>
              {REMINDER_TIMES.map(({ value, label }) => (
                <label key={value} htmlFor={`reminder-time-${value}`} className={styles.checkboxLabel}>
                  <input
                    id={`reminder-time-${value}`}
                    type="checkbox"
                    checked={formData.reminder_times.includes(value)}
                    onChange={(e) => {
                      const newTimes = e.target.checked
                        ? [...formData.reminder_times, value]
                        : formData.reminder_times.filter(t => t !== value);
                      handleFieldSave('reminder_times', newTimes);
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            <span className={styles.helperText}>
              Reminders will only be sent if you haven't made your locks for the week.
              "Gameday Mornings" sends at 9AM ET, "Last Chance" sends 1 hour before the last lockable game.
            </span>
          </div>
        </div>
      </section>

      {/* Notes Section */}
      <section className={styles.section}>
        <h3>Personal Notes</h3>
        <EditableField
          label="Notes"
          value={formData.user_notes}
          name="user_notes"
          type="textarea"
          onSave={handleFieldSave}
          helperText="This is a notes field for adding any information you'd like. If you choose to input details such as your preferred payment service (e.g., Venmo, CashApp, Zelle, PayPal, GPay) and the associated username, please note that this app is a social tool and does not condone or facilitate gambling in any form. All notes are encrypted in the database for your privacy."
        />
      </section>

      <VersionFooter />
    </div>
  );
};

export default UserSettingsPage;
